import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField, 
  Grid, 
  MenuItem,
  Typography,
  IconButton,
  InputAdornment,
  Divider,
  Box,
  Alert
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';

const SATUAN_OPTIONS = ['m1', 'm2', 'm3', 'ls', 'unit', 'pcs', 'set', 'titik', 'EA'];
const TIPE_INPUT_OPTIONS = ['manual', 'autofill'];

const MasterJobForm = ({ open, onClose, onSubmit, masterJob, mode = 'create', categories = [] }) => {
  // Inicializar formulario con react-hook-form
  const { 
    control, 
    handleSubmit, 
    reset, 
    setValue,
    watch,
    formState: { errors, isSubmitting }
  } = useForm({
    defaultValues: {
      kode_pekerjaan: '',
      nama_pekerjaan: '',
      kategori: '',
      volume_default: 0,
      satuan: 'm3',
      tipe_input: 'manual'
    }
  });
  
  // Estado para manejar categorías nuevas
  const [newCategory, setNewCategory] = useState('');
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  
  // Categorías únicas del formulario con opción para añadir nueva
  const formCategories = [
    ...new Set([
      ...categories,
      ...(masterJob?.kategori ? [masterJob.kategori] : [])
    ])
  ].sort();
  
  // Inicializar formulario con datos del trabajo maestro en modo edición
  useEffect(() => {
    if (masterJob && mode === 'edit') {
      reset({
        kode_pekerjaan: masterJob.kode_pekerjaan || '',
        nama_pekerjaan: masterJob.nama_pekerjaan || '',
        kategori: masterJob.kategori || '',
        volume_default: masterJob.volume_default || 0,
        satuan: masterJob.satuan || 'm3',
        tipe_input: masterJob.tipe_input || 'manual'
      });
    } else {
      reset({
        kode_pekerjaan: '',
        nama_pekerjaan: '',
        kategori: '',
        volume_default: 0,
        satuan: 'm3',
        tipe_input: 'manual'
      });
    }
  }, [masterJob, mode, reset]);
  
  // Manejar cierre del formulario
  const handleClose = () => {
    reset();
    setNewCategory('');
    setShowNewCategoryInput(false);
    onClose();
  };
  
  // Manejar envío del formulario
  const handleFormSubmit = (data) => {
    onSubmit(data);
  };
  
  // Manejar selección o creación de categoría
  const handleCategoryChange = (e) => {
    const value = e.target.value;
    if (value === 'add_new') {
      setShowNewCategoryInput(true);
    } else {
      setValue('kategori', value);
      setShowNewCategoryInput(false);
    }
  };
  
  // Manejar guardado de nueva categoría
  const handleSaveNewCategory = () => {
    if (newCategory.trim()) {
      setValue('kategori', newCategory.trim());
      setShowNewCategoryInput(false);
      setNewCategory('');
    }
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
        }
      }}
    >
      <DialogTitle sx={{ bgcolor: '#FFF8E1', color: '#5D4037', fontWeight: 'bold', p: 2 }}>
        {mode === 'create' ? 'Add New Work Item' : 'Edit Work Item'}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: '#5D4037'
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 3 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          {mode === 'create' 
            ? 'Enter new work item data. Work item code must be unique and follow X.X.X.XX format.' 
            : 'Edit work item data. Please make sure the data entered is correct.'
          }
        </Alert>
        
        <form id="master-job-form" onSubmit={handleSubmit(handleFormSubmit)}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom sx={{ color: '#5D4037', fontWeight: 'medium' }}>
                Work Item Code *
              </Typography>
              <Controller
                name="kode_pekerjaan"
                control={control}
                rules={{ 
                  required: 'Work item code is required',
                  pattern: {
                    value: /^\d+\.\d+\.\d+\.\d+$/,
                    message: 'Code format must be X.X.X.XX (example: 1.1.1.01)'
                  }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    placeholder="Example: 1.1.1.01"
                    variant="outlined"
                    error={!!errors.kode_pekerjaan}
                    helperText={errors.kode_pekerjaan?.message}
                    disabled={mode === 'edit'} // Kode tidak dapat editarse en modo edición
                    InputProps={{
                      sx: { bgcolor: mode === 'edit' ? '#f5f5f5' : 'white' }
                    }}
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom sx={{ color: '#5D4037', fontWeight: 'medium' }}>
                Work Item Name *
              </Typography>
              <Controller
                name="nama_pekerjaan"
                control={control}
                rules={{ 
                  required: 'Work item name is required',
                  minLength: {
                    value: 3,
                    message: 'Work item name must be at least 3 characters'
                  },
                  maxLength: {
                    value: 255,
                    message: 'Work item name must be maximum 255 characters'
                  }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    placeholder="Enter work item name"
                    variant="outlined"
                    error={!!errors.nama_pekerjaan}
                    helperText={errors.nama_pekerjaan?.message}
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom sx={{ color: '#5D4037', fontWeight: 'medium' }}>
                Category / Sub Division *
              </Typography>
              {!showNewCategoryInput ? (
                <Controller
                  name="kategori"
                  control={control}
                  rules={{ required: 'Category is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      fullWidth
                      variant="outlined"
                      error={!!errors.kategori}
                      helperText={errors.kategori?.message}
                      onChange={(e) => handleCategoryChange(e)}
                    >
                      {formCategories.map((cat) => (
                        <MenuItem key={cat} value={cat}>
                          {cat}
                        </MenuItem>
                      ))}
                      <Divider />
                      <MenuItem value="add_new">
                        <Typography color="primary">+ Add New Category</Typography>
                      </MenuItem>
                    </TextField>
                  )}
                />
              ) : (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Enter new category"
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    error={!newCategory.trim()}
                    helperText={!newCategory.trim() ? 'Category cannot be empty' : ''}
                  />
                  <Button 
                    variant="contained" 
                    onClick={handleSaveNewCategory}
                    disabled={!newCategory.trim()}
                    sx={{ 
                      bgcolor: '#F0C14B', 
                      color: '#5D4037',
                      '&:hover': { bgcolor: '#DDA726' },
                      minWidth: '100px'
                    }}
                  >
                    Save
                  </Button>
                  <Button 
                    variant="outlined" 
                    onClick={() => {
                      setShowNewCategoryInput(false);
                      setNewCategory('');
                    }}
                    sx={{ 
                      color: '#5D4037', 
                      borderColor: '#5D4037',
                      '&:hover': { bgcolor: '#F9F5EB' }
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              )}
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom sx={{ color: '#5D4037', fontWeight: 'medium' }}>
                Default Volume
              </Typography>
              <Controller
                name="volume_default"
                control={control}
                rules={{ 
                  min: {
                    value: 0,
                    message: 'Volume cannot be negative'
                  }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="number"
                    variant="outlined"
                    error={!!errors.volume_default}
                    helperText={errors.volume_default?.message}
                    InputProps={{
                      inputProps: { min: 0, step: 0.01 }
                    }}
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom sx={{ color: '#5D4037', fontWeight: 'medium' }}>
                Unit (BoQ) *
              </Typography>
              <Controller
                name="satuan"
                control={control}
                rules={{ required: 'Unit is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    variant="outlined"
                    error={!!errors.satuan}
                    helperText={errors.satuan?.message}
                  >
                    {SATUAN_OPTIONS.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom sx={{ color: '#5D4037', fontWeight: 'medium' }}>
                Input Type
              </Typography>
              <Controller
                name="tipe_input"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    variant="outlined"
                  >
                    {TIPE_INPUT_OPTIONS.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option === 'manual' ? 'Manual' : 'Auto Fill'}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      
      <DialogActions sx={{ p: 2, bgcolor: '#FAFAFA' }}>
        <Button 
          onClick={handleClose}
          sx={{ color: '#5D4037' }}
        >
          Cancel
        </Button>
        <Button 
          type="submit"
          form="master-job-form"
          variant="contained"
          disabled={isSubmitting}
          sx={{ 
            bgcolor: '#F0C14B', 
            color: '#5D4037',
            '&:hover': { bgcolor: '#DDA726' }
          }}
        >
          {mode === 'create' ? 'Add' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MasterJobForm; 